/**
 * FicheTabSource.tsx
 * ───────────────────
 * Onglet "Texte intégral" : affichage du texte brut depuis Judilibre.
 */

import { useTranslation } from 'react-i18next';
import { Loader } from '@/components/Loader';
import type { PublicFiche } from './types';

interface Props {
  fiche:         PublicFiche;
  source:        { text: string; legifrance_url: string | null } | null;
  loadingSource: boolean;
  sourceError:   boolean;
}

export function FicheTabSource({ fiche, source, loadingSource, sourceError }: Props) {
  const { t } = useTranslation();
  if (loadingSource) return (
    <div className="flex flex-col items-center justify-center py-24 gap-4">
      <Loader className="!size-8" />
      <p className="text-sm text-muted-foreground">{t('fiche.source.loading')}</p>
    </div>
  );

  if (sourceError) return (
    <div className="text-center py-24 text-muted-foreground">
      <p className="text-5xl mb-4">⚠️</p>
      <p className="font-semibold text-foreground mb-1">{t('fiche.source.unavailable')}</p>
      <p className="text-sm">{t('fiche.source.not_in_db')}</p>
    </div>
  );

  if (!source) return null;

  return (
    <div className="space-y-4">
      {source.legifrance_url && (
        <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50 border">
          <span className="text-sm text-muted-foreground">
            {t('fiche.source.appeal_no')} <span className="font-mono font-medium text-foreground">{fiche.number || fiche.ecli}</span>
          </span>
          <a
            href={source.legifrance_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
          >
            {t('fiche.source.view_on')}
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      )}
      <div className="rounded-xl border bg-muted/20 p-5 overflow-auto max-h-[65vh]">
        <pre className="text-xs leading-relaxed whitespace-pre-wrap font-mono text-foreground">
          {source.text}
        </pre>
      </div>
    </div>
  );
}